import { type CSSProperties, useMemo, useState } from 'react';
import classNames from 'clsx';
import { diffJson } from 'diff';

import { groupChangesForCollapse } from './diffCollapse';
import { annotateEpochTimestamps as annotateEpochs } from './epochAnnotations';
import { diffJsonStructurally, parseJsonPair } from './jsonStructuralDiff';
import type { RenderText } from './types';
import { renderCollapsibleDiff, renderDiff } from './unifiedDiffRenderer';
import { diffByLine, diffJSONByLine, lineNumberGutterExtraWidth, stringifyForDiff } from './utils';

import styles from './Diff.module.css';

export type DiffViewProps = {
  original: string;
  compare: string;
  showLineNumbers?: boolean;
  includeSubDiffs?: boolean;
  title?: string;
  isJSON?: boolean;
  className?: string;
  contextLines?: number;
  /**
   * When true, millisecond epoch values in the diff render the equivalent local time in a tooltip.
   * Intended for diffs of JSON payloads that embed timestamps, such as date-based targeting rules.
   */
  annotateEpochTimestamps?: boolean;
  /**
   * When true, JSON documents are compared by key path instead of by line, so an inserted or
   * removed key no longer makes its siblings look rewritten. Falls back to the line-based diff
   * when either side is not valid JSON.
   */
  alignJsonByKeyPath?: boolean;
};

export { stringifyForDiff };

export function Diff({
  original,
  compare,
  showLineNumbers = true,
  title,
  includeSubDiffs = false,
  isJSON = false,
  className,
  contextLines = 0,
  annotateEpochTimestamps = false,
  alignJsonByKeyPath = false,
}: DiffViewProps) {
  const isSameDocument = useMemo(() => original === compare, [original, compare]);

  const diff = useMemo(() => {
    if (isJSON) {
      if (alignJsonByKeyPath) {
        const parsed = parseJsonPair(original, compare);
        if (parsed) {
          return diffJsonStructurally(parsed[0], parsed[1]);
        }
      }

      if (showLineNumbers) {
        return diffJSONByLine(original, compare);
      }

      return diffJson(original, compare);
    }

    return diffByLine(original, compare);
  }, [original, compare, isJSON, showLineNumbers, alignJsonByKeyPath]);

  const groups = useMemo(() => {
    return contextLines ? groupChangesForCollapse(diff, contextLines) : [];
  }, [diff, contextLines]);

  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());

  const handleToggleExpand = (groupIndex: number) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupIndex)) {
        newSet.delete(groupIndex);
      } else {
        newSet.add(groupIndex);
      }
      return newSet;
    });
  };

  const shouldRenderGroupedDiff = contextLines > 0;

  const gutterStyle = useMemo(
    () => ({ '--diff-gutter-extra': lineNumberGutterExtraWidth(diff, shouldRenderGroupedDiff) }) as CSSProperties,
    [diff, shouldRenderGroupedDiff],
  );

  const renderText: RenderText | undefined = annotateEpochTimestamps ? annotateEpochs : undefined;

  return (
    <div
      className={classNames(styles.diffView, { [styles.diffViewSameDoc]: isSameDocument })}
      style={showLineNumbers ? gutterStyle : undefined}
    >
      {title && (
        <div className={styles.controls}>
          <div className={styles.title}>{title}</div>
        </div>
      )}
      <div className={classNames(styles.diff, className)} data-test-id="diff-container">
        {shouldRenderGroupedDiff
          ? renderCollapsibleDiff(
              groups,
              expandedGroups,
              showLineNumbers,
              includeSubDiffs,
              renderText,
              contextLines,
              handleToggleExpand,
            )
          : renderDiff(diff, showLineNumbers, includeSubDiffs, renderText)}
      </div>
    </div>
  );
}
